// امضای رضایت‌نامه (AC-DOC-041.1، 043.1، 043.2): متن کامل با الزام پیمایش تا انتها، تیک همه بندهای الزامی و امضای هر نقش لازم (مراجعه‌کننده
// یا ولی قانونی معتبر، شاهد کارکن، ارائه‌دهنده). مشترک بین پرونده (SCR-STAFF-604)، حالت تبلت (SCR-TABLET-001) و امضای از راه دور (SCR-PUBLIC-011).
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { DocConsentTextView, DocSignerOptionView } from '@phoenix/api-client';
import { useTranslation } from '@phoenix/i18n';
import { SampleBadge, SignaturePad, type SignatureValue } from '@phoenix/ui';
import { useCallback, useEffect, useRef, useState, type UIEvent } from 'react';

export interface ConsentReadState {
  scrolledToEnd: boolean;
  acceptedClauses: string[];
}

/** متن رضایت با پیمایش اجباری و بندهای تأییدی. */
export function ConsentReader({ text, isSample, onChange }: { text: DocConsentTextView; isSample: boolean; onChange: (state: ConsentReadState) => void }) {
  const { t } = useTranslation('doc');
  const box = useRef<HTMLDivElement | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [accepted, setAccepted] = useState<string[]>([]);

  const check = useCallback((element: HTMLDivElement | null) => {
    if (element && element.scrollTop + element.clientHeight >= element.scrollHeight - 8) {
      setScrolled(true);
    }
  }, []);
  useEffect(() => check(box.current), [check, text]);
  useEffect(() => onChange({ scrolledToEnd: scrolled, acceptedClauses: accepted }), [scrolled, accepted, onChange]);

  return (
    <Stack spacing={1.5} data-testid="consent-reader" data-scrolled={scrolled ? 'true' : 'false'}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="h6" component="h3">
          {text.title}
        </Typography>
        {isSample ? <SampleBadge /> : null}
      </Stack>
      <Box
        ref={box}
        tabIndex={0}
        role="region"
        aria-label={text.title}
        onScroll={(event: UIEvent<HTMLDivElement>) => check(event.currentTarget)}
        sx={{ maxHeight: 320, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 2, whiteSpace: 'pre-wrap', lineHeight: 2 }}
        data-testid="consent-text"
      >
        <Typography component="div" sx={{ lineHeight: 2 }}>
          {text.body}
        </Typography>
        {text.risks ? (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              {t('doc:consent.risks')}
            </Typography>
            <Typography component="div" sx={{ lineHeight: 2 }}>
              {text.risks}
            </Typography>
          </>
        ) : null}
      </Box>
      {!scrolled ? <Alert severity="info">{t('doc:consent.scroll_hint')}</Alert> : null}
      <Stack>
        {text.clauses.map((clause) => (
          <FormControlLabel
            key={clause.key}
            disabled={!scrolled}
            control={
              <Checkbox
                checked={accepted.includes(clause.key)}
                onChange={(event) => setAccepted((current) => (event.target.checked ? [...current, clause.key] : current.filter((key) => key !== clause.key)))}
                slotProps={{ input: { 'data-testid': `consent-clause-${clause.key}` } as never }}
              />
            }
            label={clause.required ? `${clause.text} *` : clause.text}
          />
        ))}
      </Stack>
    </Stack>
  );
}

export interface RoleSignature {
  role: string;
  signerPatientId: string | null;
  image: string;
  strokes: SignatureValue['strokes'];
}

/** امضای نقش‌های لازم (ولی معتبر از PAT؛ شاهد و ارائه‌دهنده کاربر جاری). */
export function RoleSignatures({ roles, signers, onChange }: { roles: readonly string[]; signers: readonly DocSignerOptionView[]; onChange: (signatures: RoleSignature[]) => void }) {
  const { t } = useTranslation('doc');
  const guardians = signers.filter((option) => option.role === 'Guardian');
  const [guardian, setGuardian] = useState<string>(guardians[0]?.patientId ?? '');
  const [values, setValues] = useState<Record<string, SignatureValue | null>>({});

  useEffect(() => {
    onChange(
      roles
        .filter((role) => values[role])
        .map((role) => ({
          role,
          signerPatientId: role === 'Guardian' ? guardian || null : null,
          image: values[role]!.image,
          strokes: values[role]!.strokes,
        })),
    );
  }, [roles, values, guardian, onChange]);

  return (
    <Stack spacing={2}>
      {roles.includes('Guardian') && guardians.length === 0 ? (
        <Alert severity="error" data-testid="consent-no-guardian">
          {t('doc:consent.no_guardian')}
        </Alert>
      ) : null}
      {roles.includes('Guardian') && guardians.length > 0 ? (
        <TextField select label={t('doc:consent.guardian')} value={guardian} onChange={(event) => setGuardian(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'consent-guardian' } }}>
          {guardians.map((option) => (
            <MenuItem key={option.patientId ?? option.name} value={option.patientId ?? ''}>
              {option.name} {option.relationship ? `(${t(`doc:relationship.${option.relationship}`, { defaultValue: option.relationship })})` : ''}
            </MenuItem>
          ))}
        </TextField>
      ) : null}
      {roles.map((role) => (
        <Paper key={role} variant="outlined" sx={{ p: 1.5 }}>
          <SignaturePad
            label={t(`doc:signer_role.${role}`)}
            testId={`consent-signature-${role}`}
            onChange={(value) => setValues((current) => ({ ...current, [role]: value }))}
          />
        </Paper>
      ))}
    </Stack>
  );
}

export interface ConsentSignFormProps {
  text: DocConsentTextView;
  isSample: boolean;
  roles: readonly string[];
  signers: readonly DocSignerOptionView[];
  busy: boolean;
  error?: unknown;
  onSign: (input: { scrolledToEnd: boolean; acceptedClauses: string[]; signatures: RoleSignature[] }) => void;
  renderError?: (error: unknown) => React.ReactNode;
}

export function ConsentSignForm({ text, isSample, roles, signers, busy, error, onSign, renderError }: ConsentSignFormProps) {
  const { t } = useTranslation('doc');
  const [read, setRead] = useState<ConsentReadState>({ scrolledToEnd: false, acceptedClauses: [] });
  const [signatures, setSignatures] = useState<RoleSignature[]>([]);
  const requiredClauses = text.clauses.filter((clause) => clause.required).map((clause) => clause.key);
  const ready =
    read.scrolledToEnd &&
    requiredClauses.every((key) => read.acceptedClauses.includes(key)) &&
    roles.every((role) => signatures.some((signature) => signature.role === role));

  return (
    <Stack spacing={2} data-testid="consent-sign-form">
      <ConsentReader text={text} isSample={isSample} onChange={setRead} />
      <RoleSignatures roles={roles} signers={signers} onChange={setSignatures} />
      {error && renderError ? renderError(error) : null}
      <Button variant="contained" disabled={!ready || busy} onClick={() => onSign({ ...read, signatures })} data-testid="consent-sign" sx={{ alignSelf: 'flex-start' }}>
        {t('doc:consent.sign')}
      </Button>
      {!ready ? (
        <Typography variant="caption" color="text.secondary">
          {t('doc:consent.sign_hint')}
        </Typography>
      ) : null}
    </Stack>
  );
}
